import { Component, OnInit, computed, inject } from '@angular/core';
import { InstructionsComponent } from '@r1-compass-library/components';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import {
    AddDivisionDialogComponent,
    AddDivisionDialogFormData,
    DivisionDialogData,
} from '../add-division-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import {
    GenericConfirmationDialogComponent,
    GenericConfirmationDialogData,
} from '@r1-compass-library/components/dialog/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { provideComponentStore } from '@ngrx/component-store';
import { AdminDivisionStore, DivisionTuple } from './admin-division.store';
import { filterNil } from 'ngxtension/filter-nil';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { BehaviorSubject } from 'rxjs';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { AdminDivisionSaveGuard } from '@r1-compass-library/guards/save-guards/admin-division.guard';

interface DivisionViewTuple extends DivisionTuple {
    statusClass: string;
}

@Component({
    selector: 'r1-compass-app-admin-divisions',
    templateUrl: './admin-divisions.component.html',
    styleUrl: './admin-divisions.component.scss',
    imports: [InstructionsComponent, MatButtonModule, MatIconModule, MatMenuModule],
    providers: [provideComponentStore(AdminDivisionStore)],
})
export class AdminDivisionsComponent implements OnInit, AdminDivisionSaveGuard {
    readonly #adminDivisionStore = inject(AdminDivisionStore);
    readonly #loggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #dialog = inject(MatDialog);
    readonly #formSaveWarningStore = inject(SaveGuardStore);

    canDeactivate$ = new BehaviorSubject({ dirtied: false, valid: true });
    saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningMisc;

    constructor() {
        this.saveGuardHandlerFn({
            canDeactivate$: this.canDeactivate$,
        });
    }

    protected enabledDivisions = computed<DivisionViewTuple[]>(() => {
        return this.#adminDivisionStore
            .divisionTuples()
            .filter(
                tuple =>
                    tuple.divisionConfig?.status === 'Enabled' ||
                    tuple.divisionConfig?.status === 'Pending' ||
                    tuple.divisionConfig?.status === 'Paused'
            )
            .map(tuple => ({
                ...tuple,
                statusClass: tuple.divisionConfig?.status?.toLowerCase() ?? '',
            }))
            .sort(function (a, b) {
                return a.iamDivision.divisionCode!.localeCompare(b.iamDivision.divisionCode!);
            });
    });
    protected disabledDivisions = computed<DivisionViewTuple[]>(() => {
        return this.#adminDivisionStore
            .divisionTuples()
            .filter(tuple => tuple.divisionConfig?.status === 'Disabled')
            .map(tuple => ({
                ...tuple,
                statusClass: 'disabled',
            }))
            .sort(function (a, b) {
                return a.iamDivision.divisionCode!.localeCompare(b.iamDivision.divisionCode!);
            });
    });

    ngOnInit(): void {
        this.#adminDivisionStore.getDivisionTuples();
    }

    onToggleDivisionEnable(enable: boolean, tuple: DivisionTuple) {
        if (enable) {
            tuple.divisionConfig.status = 'Enabled';
            tuple.divisionConfig.updatedBy = this.#loggedInUserStore.user()!.id;
            this.canDeactivate$.next({ dirtied: true, valid: true });
            this.#adminDivisionStore.updateTuple(tuple);
        } else {
            const dialogRef = this.#dialog.open<GenericConfirmationDialogComponent, GenericConfirmationDialogData>(
                GenericConfirmationDialogComponent,
                {
                    width: '400px',
                    disableClose: true,
                    data: {
                        confirmButton: {
                            color: 'primary',
                            type: 'stroked',
                        },
                        cancelButton: {
                            color: 'black',
                            type: 'outlined',
                        },
                        messageContainsHtml: true,
                        message:
                            '<p>Disabling a division will stop all existing plays from processing and will no longer send data to Salesforce.</p>',
                    },
                }
            );
            dialogRef.afterClosed().subscribe(confirmation => {
                if (confirmation) {
                    this.canDeactivate$.next({ dirtied: true, valid: true });
                    tuple.divisionConfig.status = 'Disabled';
                    tuple.divisionConfig.updatedBy = this.#loggedInUserStore.user()!.id;
                    this.#adminDivisionStore.updateTuple(tuple);
                }
            });
        }
    }

    onToggleDivisionPause(tuple: DivisionTuple) {
        const isPaused = tuple.divisionConfig.status === 'Paused';
        const dialogRef = this.#dialog.open<GenericConfirmationDialogComponent, GenericConfirmationDialogData>(
            GenericConfirmationDialogComponent,
            {
                width: '400px',
                disableClose: true,
                data: {
                    confirmButton: {
                        text: isPaused ? 'Resume' : 'Pause',
                        type: 'stroked',
                    },
                    cancelButton: {
                        text: 'Cancel',
                        type: 'flat',
                        color: 'white',
                        backgroundColor: 'black',
                    },
                    messageContainsHtml: false,
                    message: 'This will affect all plays in this division. Are you sure?',
                },
            }
        );

        dialogRef.afterClosed().subscribe(confirmation => {
            if (confirmation) {
                this.canDeactivate$.next({ dirtied: true, valid: true });
                const statusToSet = isPaused ? 'Enabled' : 'Paused';
                tuple.divisionConfig.status = statusToSet;
                tuple.divisionConfig.updatedBy = this.#loggedInUserStore.user()!.id;
                this.#adminDivisionStore.updateTuple(tuple);
            }
        });
    }

    onEditDivision(action: 'add' | 'rename', tuple?: DivisionTuple) {
        const dialogRef = this.#dialog.open<AddDivisionDialogComponent, DivisionDialogData, AddDivisionDialogFormData>(
            AddDivisionDialogComponent,
            {
                width: '400px',
                disableClose: true,
                data: {
                    iamDivision: tuple?.iamDivision,
                },
            }
        );
        dialogRef
            .afterClosed()
            .pipe(filterNil())
            .subscribe(divisionData => {
                this.canDeactivate$.next({ dirtied: true, valid: true });
                const createdBy = action === 'add' ? this.#loggedInUserStore.user()?.id : undefined;
                const updatedBy = this.#loggedInUserStore.user()!.id;
                if (tuple?.iamDivision) {
                    tuple.iamDivision.divisionCode = divisionData?.divisionCode;
                    tuple.iamDivision.description = divisionData?.description;
                    tuple.iamDivision.updatedBy = updatedBy;
                    if (createdBy) {
                        tuple.iamDivision.createdBy = createdBy;
                    }
                    this.#adminDivisionStore.updateTuple(tuple);
                } else {
                    const addTuple = {
                        ...divisionData,
                        createdBy: createdBy,
                        updatedBy: updatedBy,
                    };
                    this.#adminDivisionStore.addNewTuple(addTuple);
                }
            });
    }

    onSave() {
        this.canDeactivate$.next({ dirtied: false, valid: true });
        this.#adminDivisionStore.saveTuples();
    }
}
